import {
  api,
  isApiError,
  type SpEntry,
  type SpIndexJob,
  type SpIndexProgressEntry,
  type SpIndexSelection,
  type SpResolved,
  type SpSite,
} from './api'

/**
 * SharePoint site / library / folder browsing for the bid-site picker and browser
 * (components/SharePointBrowser.tsx) — through NIOBE'S BACKEND, not from the browser.
 *
 * These three reads used to go straight to `graph.microsoft.com` with a delegated Graph token this
 * tab held itself (lib/graphAuth.ts, MSAL, `Sites.ReadWrite.All`). They now go to
 * `/v1/bids/:bid/sharepoint/*`, and the backend exchanges the member's Niobe token for a Graph one
 * ON THEIR BEHALF before calling Graph.
 *
 * THE IDENTITY IS UNCHANGED, and that is the whole reason it is an on-behalf-of exchange rather
 * than the server's own app credential: the Graph token still acts AS the member, so SharePoint's
 * per-user ACL still decides every row — someone who cannot open a folder in SharePoint cannot open
 * it here, and a 403 remains an ordinary answer rather than a fault. Niobe adds exactly one gate of
 * its own in front of it, membership of the bid, so that holding a Niobe token does not turn this
 * into a general-purpose SharePoint search over the tenant.
 *
 * WHY MOVE IT. A token in the browser is a token to keep alive in the browser, and keeping this one
 * alive cost the workspace three mechanisms whose only job was to hide a second sign-in: a silent
 * MSAL session opened on mount, one automatic full-page redirect to Entra when silence ran out, and
 * a sessionStorage marker in the dashboard card to remember what the member had clicked before that
 * redirect threw the page away. None of it was visible when it worked and all of it was visible
 * when it did not. The member has already signed in to Niobe; the backend can do the rest.
 *
 * EVERY CALL IS BID-SCOPED because the permission authorising it is. There is no "browse
 * SharePoint" here — only "browse SharePoint for THIS bid".
 */

/**
 * The row shapes are declared next to the endpoints that return them (lib/api.ts, with BidSite),
 * and re-exported here because every consumer imports them from this module — it is the browsing
 * vocabulary, wherever the wire types happen to live.
 */
export type { SpEntry, SpIndexJob, SpIndexProgressEntry, SpIndexSelection, SpResolved, SpSite }

/**
 * A failed browse, carrying the status AND the backend's code so a caller can tell "no access"
 * from "broken" — and, crucially, one refusal from the other.
 *
 * The status alone is not enough, and this is the whole reason `code` is here. TWO different
 * refusals arrive as 403: `forbidden` means Niobe refused, because the caller is not on this bid;
 * `sharepoint_forbidden` means SharePoint refused a member who IS on it. They have opposite
 * remedies. Reading the bare status would send someone who is simply not on the bid to
 * SharePoint's request-access page, asking them for a permission they may already hold, while
 * saying nothing about the reason they were actually turned away.
 *
 * `0` is a transport failure — the request never got an answer at all — and `502` is the backend
 * unable to reach SharePoint or complete the token exchange.
 *
 * Kept named GraphError even though this browser no longer speaks to Graph: what it reports is
 * still SharePoint's verdict, relayed.
 */
export class GraphError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The backend's `error.code`. '' when it sent none, or when nothing answered at all. */
    readonly code: string = '',
  ) {
    super(message)
    this.name = 'GraphError'
  }
}

/**
 * Run one browse call, turning any failure into a {@link GraphError}.
 *
 * Two of lib/api.ts's failure modes are NOT `ApiError` and must not be read as one: a network throw
 * (no `status` at all) and a 401, which api.ts treats as "the Niobe session is dead" — it clears the
 * token and throws a bare Error. Both land on 0 here, because neither is SharePoint's answer about
 * this folder. The backend is required not to relay a Graph auth failure as a 401 for exactly that
 * reason: it would sign the member out of Niobe over a SharePoint hiccup.
 */
async function browse<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (e) {
    if (isApiError(e)) {
      // The backend's own wording when it sent one — it knows whether SharePoint refused, went
      // missing or could not be reached, and saying so beats a status code.
      throw new GraphError(
        e.status,
        e.serverMessage || `SharePoint could not be read (${e.status}).`,
        e.code,
      )
    }
    throw new GraphError(0, 'Cannot reach Niobe to read SharePoint.')
  }
}

/**
 * Sites matching a search term, within the bid the member is working on.
 *
 * A term is REQUIRED: "list every site" is an application-only Graph call and 403s on a delegated
 * token. The empty term is answered here rather than travelling — the backend answers it the same
 * way, so this saves a round trip and states the rule at the surface that shows it.
 */
export async function searchSites(bid: string, term: string): Promise<SpSite[]> {
  const q = term.trim()
  if (!q) return []
  return (await browse(() => api.spSites(bid, q))).sites
}

/**
 * Resolve a pasted SharePoint address — sharing link, library/folder/file URL or bare site URL —
 * to the deepest level it identifies, as the member. `null` means the backend could not open it
 * with their account (unparseable, or nothing it points at is reachable): an answer, not a fault.
 */
export async function resolveLink(bid: string, url: string): Promise<SpResolved | null> {
  return (await browse(() => api.spResolve(bid, url.trim()))).resolved
}

/** The site's document libraries — the level below a site, and where a bid folder must live. */
export async function listLibraries(bid: string, siteId: string): Promise<SpEntry[]> {
  return (await browse(() => api.spDrives(bid, siteId))).entries
}

/**
 * What is inside a library (`itemId: 'root'`) or one of its folders. Files included.
 *
 * Folders before files, then by name, numeric-aware and case-insensitive — the order every file
 * explorer uses. It is the BACKEND's rule now, applied where the rows are assembled, so nothing here
 * sorts them a second time: two sorts is one place for the two to disagree.
 */
export async function listChildren(bid: string, driveId: string, itemId: string): Promise<SpEntry[]> {
  return (await browse(() => api.spChildren(bid, driveId, itemId))).entries
}

/**
 * Create a folder in the bid's SharePoint drive — the "+ New folder" affordance in
 * SharePointBrowser's `canCreateFolder` mode (the export destination picker). Editor/admin only
 * server-side; a refusal here (e.g. `sharepoint_conflict` on a name collision) is for the caller to
 * render, same as every other write in this module.
 */
export async function createFolder(bid: string, driveId: string, parentItemId: string, name: string): Promise<SpEntry> {
  return (await browse(() => api.spCreateFolder(bid, driveId, parentItemId, name))).entry
}

/**
 * Click-to-index: send the member's selection (files, or folders to walk recursively) into the
 * bid's own KB. Detached — resolves with a job id the caller polls with {@link indexJob} — because
 * a folder can hold enough files that the run outlasts one HTTP request.
 */
export async function startIndex(
  bid: string,
  driveId: string,
  items: SpIndexSelection[],
): Promise<{ jobId: string; status: SpIndexJob['status'] }> {
  return browse(() => api.spIndex(bid, driveId, items))
}

/** Poll one click-to-index job. */
export async function indexJob(bid: string, jobId: string): Promise<SpIndexJob> {
  return browse(() => api.spIndexJob(bid, jobId))
}

/**
 * "Interrupt indexation": ask a running click-to-index job to stop before its next file. Files
 * already ingested are not rolled back — the next poll's `result.stopped` reads `'cancelled'`
 * once the run has actually settled. A no-op, not an error, on a job that already has.
 */
export async function cancelIndex(bid: string, jobId: string): Promise<{ ok: boolean }> {
  return browse(() => api.spCancelIndex(bid, jobId))
}

/**
 * De-index: remove one file, or an entire folder's worth, from the bid's own KB. Synchronous —
 * there is no job to poll, unlike {@link startIndex}: the walk has no download/parse/embed behind
 * it, so even a full folder answers within one request.
 */
export async function deIndex(
  bid: string,
  driveId: string,
  item: SpIndexSelection,
): Promise<{ deleted: number }> {
  return browse(() => api.spDeIndex(bid, driveId, item))
}

/**
 * Deep link to SharePoint's own request-access page for the site owning `url`.
 *
 * Niobe grants nothing here: when a member cannot open the bid site, the fix is a SharePoint
 * permission, and SharePoint renders and routes that request itself. Pure string work — no call,
 * and therefore no bid.
 */
export function requestAccessUrl(url: string): string | null {
  try {
    const parsed = new URL(url)
    const path = parsed.pathname
    // A pasted SHARING link: `/:f:/s/<name>/…` names the site directly, and `/:f:/r/sites/<name>/…`
    // carries the ordinary site path after its `/:x:/r` prefix.
    const shared = path.match(/^\/:[a-z]:\/s\/([^/]+)/i)
    if (shared) return `${parsed.origin}/sites/${shared[1]}/_layouts/15/AccessDenied.aspx`
    const match = path.replace(/^\/:[a-z]:\/r(?=\/)/i, '').match(/^(\/sites\/[^/]+)/i)
    if (!match) return null
    return `${parsed.origin}${match[1]}/_layouts/15/AccessDenied.aspx`
  } catch {
    return null
  }
}
